import Phaser from "phaser";

/**
 * Anime-spectacle layer (Dragon Ball / Naruto energy): manga impact frames,
 * radial speed lines, screen flashes, hit-stop, ki charge-up auras, and
 * Kamehameha-style energy beams. This is the "make people hype" layer.
 *
 * All visuals render above gameplay (depth 300+). UIScene still draws over
 * everything since it is a separate scene on top.
 */
export class AnimeFX {
  private scene: Phaser.Scene;
  /** Game-time ms until which gameplay logic should freeze (hit-stop). */
  hitStopUntil = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Whether gameplay should be frozen this frame. */
  isFrozen(now: number): boolean {
    return now < this.hitStopUntil;
  }

  /** Briefly freeze gameplay for punch. Tweens/FX keep running. */
  hitStop(ms: number): void {
    this.hitStopUntil = Math.max(this.hitStopUntil, this.scene.time.now + ms);
  }

  // ---------------------------------------------------------------- flashes

  /** Full-screen color flash. */
  flash(color = 0xffffff, alpha = 0.6, dur = 140): void {
    const { width, height } = this.scene.scale;
    const r = this.scene.add.rectangle(width / 2, height / 2, width, height, color, alpha);
    r.setDepth(320);
    this.scene.tweens.add({
      targets: r,
      alpha: 0,
      duration: dur,
      ease: "Quad.easeOut",
      onComplete: () => r.destroy(),
    });
  }

  // ------------------------------------------------------------ speed lines

  /**
   * Manga radial speed lines converging on / bursting from a point.
   * Short, white-hot, fade in ~180ms — the classic "impact" punctuation.
   */
  speedLines(cx: number, cy: number, color = 0xffffff, count = 16, reach = 260): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.15;
      const inner = 26 + Math.random() * 18;
      const outer = inner + reach * (0.5 + Math.random() * 0.6);
      const x1 = cx + Math.cos(a) * inner;
      const y1 = cy + Math.sin(a) * inner;
      const x2 = cx + Math.cos(a) * outer;
      const y2 = cy + Math.sin(a) * outer;
      const line = this.scene.add.line(0, 0, x1, y1, x2, y2, color, 0.9).setLineWidth(2 + Math.random() * 2);
      line.setOrigin(0, 0);
      line.setDepth(312);
      this.scene.tweens.add({
        targets: line,
        alpha: 0,
        duration: 160 + Math.random() * 120,
        ease: "Quad.easeOut",
        onComplete: () => line.destroy(),
      });
    }
  }

  /** Big anime "impact star" — a multi-point spiked white flash burst. */
  impactStar(x: number, y: number, color = 0xffffff, size = 70): void {
    const pts: number[] = [];
    const spikes = 8;
    for (let i = 0; i < spikes * 2; i++) {
      const a = (i / (spikes * 2)) * Math.PI * 2;
      const rad = i % 2 === 0 ? size : size * 0.42;
      pts.push(Math.cos(a) * rad, Math.sin(a) * rad);
    }
    const star = this.scene.add.polygon(x, y, pts, color, 0.95);
    star.setDepth(314);
    const core = this.scene.add.circle(x, y, size * 0.45, 0xffffff, 0.95).setDepth(315);
    this.scene.tweens.add({
      targets: [star, core],
      scale: { from: 0.3, to: 1.25 },
      alpha: { from: 1, to: 0 },
      duration: 240,
      ease: "Cubic.easeOut",
      onComplete: () => {
        star.destroy();
        core.destroy();
      },
    });
  }

  /** Subtle camera zoom punch — the "wham" of a big anime hit. */
  zoomPunch(scale = 1.045, dur = 90): void {
    const cam = this.scene.cameras.main;
    // Always return to 1.0 — the true resting zoom — so rapid consecutive
    // punches never ratchet the zoom upward indefinitely.
    const base = 1.0;
    cam.zoomTo(base * scale, dur, "Quad.easeOut", false, (_c, prog) => {
      if (prog === 1) cam.zoomTo(base, dur * 1.6, "Quad.easeInOut");
    });
  }

  /**
   * Full impact combo for crits / big hits: white flash, speed lines,
   * impact star, micro hit-stop, zoom punch + shake. Tune intensity 0..1.
   */
  impact(x: number, y: number, color = 0xffffff, intensity = 1): void {
    this.flash(color, 0.18 + 0.22 * intensity, 110);
    this.speedLines(x, y, 0xffffff, Math.round(12 + 8 * intensity), 180 + 120 * intensity);
    this.impactStar(x, y, color, 50 + 40 * intensity);
    this.hitStop(50 + Math.round(70 * intensity));
    this.zoomPunch(1.03 + 0.03 * intensity, 80);
    this.scene.cameras.main.shake(120, 0.004 + 0.006 * intensity);
  }

  // ------------------------------------------------------------- charge-up

  /**
   * Ki charge-up aura at a point: an inward-collapsing energy ring + glowing
   * core that swells, telegraphing a big attack. Returns when done.
   */
  chargeAura(x: number, y: number, color: number, dur = 420): void {
    // Collapsing intake ring.
    const ring = this.scene.add.circle(x, y, 44, color, 0).setStrokeStyle(3, color, 0.9).setDepth(308);
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 1.4, to: 0.2 },
      alpha: { from: 0.9, to: 0 },
      duration: dur,
      ease: "Cubic.easeIn",
      onComplete: () => ring.destroy(),
    });
    // Swelling bright core.
    const core = this.scene.add.circle(x, y, 5, 0xffffff, 0.95).setDepth(309);
    this.scene.tweens.add({
      targets: core,
      scale: { from: 0.4, to: 2.6 },
      alpha: { from: 0.9, to: 0.2 },
      duration: dur,
      ease: "Quad.easeOut",
      onComplete: () => core.destroy(),
    });
    // Crackle sparks orbiting in.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const d = 40;
      const spark = this.scene.add.circle(x + Math.cos(a) * d, y + Math.sin(a) * d, 2.5, color, 1).setDepth(309);
      this.scene.tweens.add({
        targets: spark,
        x,
        y,
        alpha: { from: 1, to: 0 },
        duration: dur,
        ease: "Cubic.easeIn",
        onComplete: () => spark.destroy(),
      });
    }
  }

  // ---------------------------------------------------------------- beams

  /**
   * Kamehameha-style energy beam from (x1,y1) to (x2,y2): white-hot core,
   * colored glow halo, a muzzle burst at the source and an impact burst at
   * the target. Expands then fades over ~360ms.
   */
  beam(x1: number, y1: number, x2: number, y2: number, color: number, width = 14): void {
    const ang = Math.atan2(y2 - y1, x2 - x1);
    const len = Math.hypot(x2 - x1, y2 - y1);
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;

    const glow = this.scene.add.rectangle(mx, my, len, width * 2.2, color, 0.5);
    glow.setRotation(ang);
    glow.setDepth(306);
    const core = this.scene.add.rectangle(mx, my, len, width * 0.5, 0xffffff, 0.95);
    core.setRotation(ang);
    core.setDepth(307);

    // Muzzle flash at source.
    const muzzle = this.scene.add.circle(x1, y1, width * 1.4, 0xffffff, 0.9).setDepth(307);
    this.scene.tweens.add({
      targets: muzzle,
      scale: { from: 0.5, to: 1.8 },
      alpha: { from: 0.9, to: 0 },
      duration: 260,
      ease: "Cubic.easeOut",
      onComplete: () => muzzle.destroy(),
    });

    this.scene.tweens.add({
      targets: [glow, core],
      scaleY: { from: 1.4, to: 0.2 },
      alpha: { from: 1, to: 0 },
      duration: 340,
      ease: "Quad.easeIn",
      onComplete: () => {
        glow.destroy();
        core.destroy();
      },
    });

    // Impact burst at target.
    this.impactStar(x2, y2, color, width * 3);
    this.speedLines(x2, y2, color, 10, 120);
  }

  destroy(): void {
    this.hitStopUntil = 0;
  }
}
